import { useEffect, type RefObject } from 'react';

/** Phones get a full-screen panel (matches the media query in chat.css). */
export const PHONE_QUERY = '(max-width: 560px)';

export const isPhone = (): boolean => typeof window !== 'undefined' && !!window.matchMedia?.(PHONE_QUERY).matches;

/**
 * Keeps a full-screen panel on the part of the screen the user can actually see.
 * iOS doesn't shrink the layout viewport for the on-screen keyboard: it scrolls the
 * page instead, so a `position: fixed; inset: 0` panel slides up and the page shows
 * between it and the keyboard. While `active` on a phone, this pins the panel to
 * window.visualViewport (top and height, updated as the keyboard opens and closes)
 * and stops the page behind it scrolling. Larger screens are left alone.
 */
export function useVisualViewportPin(ref: RefObject<HTMLElement | null>, active: boolean): void {
  useEffect(() => {
    const el = ref.current;
    if (!active || !el || !isPhone()) return;

    const root = document.documentElement;
    const body = document.body;
    const before = { root: root.style.overflow, body: body.style.overflow };
    root.style.overflow = 'hidden';
    body.style.overflow = 'hidden';

    const vv = window.visualViewport;
    const fit = () => {
      if (!vv) return;
      el.style.top = `${vv.offsetTop}px`;
      el.style.height = `${vv.height}px`;
      el.style.bottom = 'auto';
    };
    fit();
    vv?.addEventListener('resize', fit);
    vv?.addEventListener('scroll', fit);

    return () => {
      vv?.removeEventListener('resize', fit);
      vv?.removeEventListener('scroll', fit);
      root.style.overflow = before.root;
      body.style.overflow = before.body;
      el.style.top = '';
      el.style.height = '';
      el.style.bottom = '';
    };
  }, [ref, active]);
}
